import React from "react";
import { Typography, Button, Space, Row, Col, Card, Divider, Tag, Steps, Badge } from "antd";
import { ArrowLeft, Package, User, CreditCard, Clock, CheckCircle2, AlertCircle, FileText } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const { Title, Text } = Typography;

const RefundDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    // Mock data for the refund
    const refundData = {
        id: id || '37',
        orderId: '69',
        status: 'Completed',
        refundAmount: 120.00,
        refundDate: 'Oct 21, 2024 04:50 PM',
        paymentMethod: 'Credit Card (Visa)',
        reason: 'Product arrived damaged during shipping',
        customer: {
            name: 'John Doe',
            email: 'john@example.com',
            phone: '+44 456 454 555',
            billingAddress: {
                company: 'Cox Inc',
                name: 'John Doe',
                street: '56 Wrexham Road',
                city: 'Featherstone',
                state: 'Bilzen',
                country: 'United Kingdom',
                zip: '90006'
            },
            shippingAddress: {
                company: 'Cox Inc',
                name: 'John Doe',
                street: '56 Wrexham Road',
                city: 'Featherstone',
                state: 'Bilzen',
                country: 'United Kingdom',
                zip: '90006'
            }
        },
        items: [
            {
                id: 1,
                name: "AquaStride Men's Stylish Waterproof Casual Sports Shoes",
                variant: "Color: Blue, Size: 7",
                sku: "AQUASTRIDESPORTS2023-variant-41-32",
                quantity: 1,
                price: 120.00,
                image: "https://via.placeholder.com/60"
            }
        ],
        timeline: [
            { title: 'Refund Requested', description: 'Oct 20, 2024 10:30 AM', status: 'finish' },
            { title: 'Approved by Admin', description: 'Oct 20, 2024 02:15 PM', status: 'finish' },
            { title: 'Refund Processed', description: 'Oct 21, 2024 04:50 PM', status: 'finish' },
            { title: 'Completed', description: 'Funds returned to customer', status: 'finish' }
        ]
    };

    const getStatusTag = (status) => {
        switch (status) {
            case 'Completed':
                return <Tag color="success" style={{ borderRadius: '20px', padding: '2px 12px', fontWeight: 600 }}>Completed</Tag>;
            case 'Pending':
                return <Tag color="warning" style={{ borderRadius: '20px', padding: '2px 12px', fontWeight: 600 }}>Pending</Tag>;
            default:
                return <Tag color="default" style={{ borderRadius: '20px', padding: '2px 12px', fontWeight: 600 }}>{status}</Tag>;
        }
    };

    return (
        <div className="refund-details-container" style={{ padding: '32px', background: '#f8fafc', minHeight: '100vh' }}>

            {/* Navigation & Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <Space size="middle">
                    <Button
                        icon={<ArrowLeft size={18} />}
                        onClick={() => navigate(-1)}
                        style={{ border: 'none', background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                    >
                        Back
                    </Button>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Title level={3} style={{ margin: 0, fontWeight: 800, color: '#1e293b' }}>Refund #{refundData.id}</Title>
                            {getStatusTag(refundData.status)}
                        </div>
                        <Text type="secondary" style={{ fontSize: '14px' }}>Order ID: #{refundData.orderId} • Requested on {refundData.refundDate}</Text>
                    </div>
                </Space>
                <Space>
                    <Button style={{ borderRadius: '8px', fontWeight: 600 }}>Print Details</Button>
                    <Button type="primary" style={{ borderRadius: '8px', fontWeight: 600, background: '#2563eb' }}>Download Receipt</Button>
                </Space>
            </div>

            <Row gutter={[24, 24]}>
                {/* Left Column */}
                <Col xs={24} lg={16}>
                    {/* Refund Summary Card */}
                    <Card
                        bordered={false}
                        style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '24px' }}
                        title={<Space><FileText size={20} color="#2563eb" /><Text strong style={{ fontSize: '18px' }}>Refund Summary</Text></Space>}
                    >
                        <Row gutter={24}>
                            <Col span={8}>
                                <div style={{ background: '#f1f5f9', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                                    <Text type="secondary" block style={{ marginBottom: '8px' }}>Refund Amount</Text>
                                    <Title level={3} style={{ margin: 0, color: '#0f172a' }}>${refundData.refundAmount.toFixed(2)}</Title>
                                </div>
                            </Col>
                            <Col span={16}>
                                <div style={{ padding: '10px 0' }}>
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Reason for Refund</Text>
                                            <Text strong style={{ color: '#ef4444' }}>{refundData.reason}</Text>
                                        </div>
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Payment Method</Text>
                                            <Space><CreditCard size={16} /><Text strong>{refundData.paymentMethod}</Text></Space>
                                        </div>
                                    </Space>
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    {/* Items Card */}
                    <Card
                        bordered={false}
                        style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '24px' }}
                        title={<Space><Package size={20} color="#2563eb" /><Text strong style={{ fontSize: '18px' }}>Refunded Items</Text></Space>}
                    >
                        {refundData.items.map((item, index) => (
                            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: index === 0 ? '0' : '16px 0', borderTop: index === 0 ? 'none' : '1px solid #f1f5f9' }}>
                                <Space size="large">
                                    <div style={{ width: 80, height: 80, background: '#f8fafc', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={item.image} alt={item.name} style={{ maxWidth: '100%' }} />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <Text strong style={{ fontSize: '16px', color: '#0f172a' }}>{item.name}</Text>
                                        <Text type="secondary" style={{ fontSize: '13px' }}>{item.variant}</Text>
                                        <Text type="secondary" style={{ fontSize: '13px' }}>SKU: {item.sku}</Text>
                                        <Badge count={`Qty: ${item.quantity}`} style={{ backgroundColor: '#f1f5f9', color: '#64748b', boxShadow: 'none', border: '1px solid #e2e8f0', marginTop: '4px' }} />
                                    </div>
                                </Space>
                                <div style={{ textAlign: 'right' }}>
                                    <Text strong style={{ fontSize: '18px', color: '#0f172a' }}>${item.price.toFixed(2)}</Text>
                                </div>
                            </div>
                        ))}
                    </Card>

                    {/* Timeline Card */}
                    <Card
                        bordered={false}
                        style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                        title={<Space><Clock size={20} color="#2563eb" /><Text strong style={{ fontSize: '18px' }}>Refund Timeline</Text></Space>}
                    >
                        <Steps
                            direction="vertical"
                            current={3}
                            size="small"
                            items={refundData.timeline.map(t => ({
                                title: <Text strong style={{ color: '#1e293b' }}>{t.title}</Text>,
                                description: <Text type="secondary" style={{ fontSize: '12px' }}>{t.description}</Text>,
                                status: t.status
                            }))}
                            style={{ padding: '12px 0' }}
                        />
                    </Card>
                </Col>

                {/* Right Column */}
                <Col xs={24} lg={8}>
                    {/* Customer Info Card */}
                    <Card
                        bordered={false}
                        style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '24px' }}
                        title={<Space><User size={20} color="#2563eb" /><Text strong style={{ fontSize: '18px' }}>Customer Information</Text></Space>}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                            <div style={{ width: 48, height: 48, borderRadius: '24px', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Text strong style={{ color: '#2563eb', fontSize: '18px' }}>JD</Text>
                            </div>
                            <div>
                                <Title level={5} style={{ margin: 0 }}>{refundData.customer.name}</Title>
                                <Text type="secondary">{refundData.customer.email}</Text>
                            </div>
                        </div>

                        <Divider style={{ margin: '16px 0' }} />

                        <div style={{ marginBottom: '20px' }}>
                            <Text strong style={{ color: '#64748b', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.05em' }}>Contact Details</Text>
                            <div style={{ marginTop: '8px' }}>
                                <Text block>{refundData.customer.phone}</Text>
                                <Text type="secondary">Customer since 2023</Text>
                            </div>
                        </div>

                        <Divider style={{ margin: '16px 0' }} />

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <Text strong style={{ color: '#64748b', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.05em' }}>Billing Address</Text>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <Text strong>{refundData.customer.billingAddress.company}</Text>
                                <Text>{refundData.customer.billingAddress.street}</Text>
                                <Text>{refundData.customer.billingAddress.city}, {refundData.customer.billingAddress.state}</Text>
                                <Text>{refundData.customer.billingAddress.country} ({refundData.customer.billingAddress.zip})</Text>
                            </div>
                        </div>

                        <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '12px', border: '1px dashed #bfdbfe' }}>
                            <Space align="start">
                                <AlertCircle size={16} color="#3b82f6" style={{ marginTop: '2px' }} />
                                <Text style={{ color: '#1e40af', fontSize: '13px' }}>This refund will be credited back to the original payment method within 3-5 business days.</Text>
                            </Space>
                        </div>
                    </Card>


                </Col>
            </Row>
        </div>
    );
};

export default RefundDetails;

